import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// =======================
// Async Thunks
// =======================

/**
 * Grant a permission to a profile on a resource
 */
export const grantPermission = createAsyncThunk(
  'permissions/grantPermission',
  async ({
    token,
    profileId,
    resourceType,
    resourceId,
    permissionLevel,
    granterId,
    expiresAt = null,
    requesterProfileId = null,
  }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      resourceId,
      permissionLevel,
      granterId,
    })
    if (expiresAt) {
      params.append('expiresAt', expiresAt)
    }

    const response = await apiCallWithHost(
      `/v2/permissions/grant?${params.toString()}`,
      { method: 'POST' },
      token,
      requesterProfileId || granterId, // Use requesterProfileId or fallback to granterId
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to grant permission')
    }
    return response.json()
  },
)

/**
 * Revoke a specific permission from a profile on a resource
 */
export const revokePermission = createAsyncThunk(
  'permissions/revokePermission',
  async ({
    token,
    profileId,
    resourceType,
    resourceId,
    permissionLevel,
    revokerId,
    requesterProfileId = null,
  }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      resourceId,
      permissionLevel,
      revokerId,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/revoke?${params.toString()}`,
      { method: 'DELETE' },
      token,
      requesterProfileId || revokerId, // Use requesterProfileId or fallback to revokerId
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to revoke permission')
    }
    return { profileId, resourceType, resourceId, permissionLevel }
  },
)

/**
 * Revoke all permissions for a profile on a resource
 */
export const revokeAllPermissions = createAsyncThunk(
  'permissions/revokeAllPermissions',
  async ({ token, profileId, resourceType, resourceId, revokerId, requesterProfileId = null }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      resourceId,
      revokerId,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/revoke-all?${params.toString()}`,
      { method: 'DELETE' },
      token,
      requesterProfileId || revokerId, // Use requesterProfileId or fallback to revokerId
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to revoke all permissions')
    }
    return { profileId, resourceType, resourceId, count: await response.json() }
  },
)

/**
 * Check if a profile has a specific permission on a resource
 */
export const checkPermission = createAsyncThunk(
  'permissions/checkPermission',
  async ({
    token,
    profileId,
    resourceType,
    resourceId,
    permissionLevel,
    requesterProfileId = null,
  }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      resourceId,
      permissionLevel,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/check?${params.toString()}`,
      {},
      token,
      requesterProfileId || profileId, // Use requesterProfileId or fallback to profileId
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to check permission')
    }
    const hasPermission = await response.json()
    return { profileId, resourceType, resourceId, permissionLevel, hasPermission }
  },
)

/**
 * Get the highest permission level a profile has on a resource
 */
export const getHighestPermission = createAsyncThunk(
  'permissions/getHighestPermission',
  async ({ token, profileId, resourceType, resourceId, requesterProfileId = null }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      resourceId,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/highest?${params.toString()}`,
      {},
      token,
      requesterProfileId || profileId, // Use requesterProfileId or fallback to profileId
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get highest permission')
    }
    const permissionLevel = await response.json()
    return { profileId, resourceType, resourceId, permissionLevel }
  },
)

/**
 * Get all active permissions for a specific resource
 */
export const getResourcePermissions = createAsyncThunk(
  'permissions/getResourcePermissions',
  async ({ token, resourceType, resourceId, requesterProfileId = null }) => {
    const params = new URLSearchParams({
      resourceType,
      resourceId,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/resource?${params.toString()}`,
      {},
      token,
      requesterProfileId,
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get resource permissions')
    }
    return response.json()
  },
)

/**
 * Get all active permissions for a specific profile
 */
export const getProfilePermissions = createAsyncThunk(
  'permissions/getProfilePermissions',
  async ({ token, profileId, requesterProfileId = null }) => {
    const response = await apiCallWithHost(
      `/v2/permissions/profile/${profileId}`,
      {},
      token,
      requesterProfileId || profileId,
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get profile permissions')
    }
    return response.json()
  },
)

/**
 * Get all resources of a type that a profile has access to
 */
export const getAccessibleResources = createAsyncThunk(
  'permissions/getAccessibleResources',
  async ({ token, profileId, resourceType, requiredPermission, requesterProfileId = null }) => {
    const params = new URLSearchParams({
      profileId,
      resourceType,
      requiredPermission,
    })

    const response = await apiCallWithHost(
      `/v2/permissions/accessible?${params.toString()}`,
      {},
      token,
      requesterProfileId || profileId,
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to get accessible resources')
    }
    return { resourceType, resourceIds: await response.json() }
  },
)

/**
 * Update sharing settings for a draft
 */
export const updateDraftSharing = createAsyncThunk(
  'permissions/updateDraftSharing',
  async ({ token, draftId, visibility, sharedWithProfiles = [], requesterProfileId }) => {
    const params = new URLSearchParams({
      visibility,
    })

    if (sharedWithProfiles.length > 0) {
      sharedWithProfiles.forEach((profileId) => {
        params.append('sharedWithProfiles', profileId)
      })
    }

    if (requesterProfileId) {
      params.append('requesterProfileId', requesterProfileId)
    }

    const response = await apiCallWithHost(
      `/v2/drafts/${draftId}/share?${params.toString()}`,
      { method: 'PUT' },
      token,
      requesterProfileId,
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update draft sharing')
    }
    return response.json()
  },
)

/**
 * Update sharing settings for a published workout
 */
export const updateWorkoutSharing = createAsyncThunk(
  'permissions/updateWorkoutSharing',
  async ({ token, workoutId, visibility, sharedWithClients = [], requesterId }) => {
    const params = new URLSearchParams({
      visibility,
    })

    if (sharedWithClients.length > 0) {
      sharedWithClients.forEach((clientId) => {
        params.append('sharedWithClients', clientId)
      })
    }

    if (requesterId) {
      params.append('requesterId', requesterId)
    }

    const response = await apiCallWithHost(
      `/v2/workouts/${workoutId}/share?${params.toString()}`,
      { method: 'PUT' },
      token,
      requesterId,
    )
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update workout sharing')
    }
    return response.json()
  },
)

// =======================
// Initial State
// =======================

const initialState = {
  // Permissions for the current resource being viewed
  currentResourcePermissions: [],
  // Permissions for the current profile
  currentProfilePermissions: [],
  // Permission check results (keyed by profileId-resourceType-resourceId-permissionLevel)
  permissionChecks: {},
  // Highest permissions (keyed by profileId-resourceType-resourceId)
  highestPermissions: {},
  // Accessible resources (keyed by profileId-resourceType-permissionLevel)
  accessibleResources: {},
  // Status tracking
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  // Individual operation statuses
  grantStatus: 'idle',
  revokeStatus: 'idle',
  checkStatus: 'idle',
}

// =======================
// Slice
// =======================

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissions: (state) => {
      state.currentResourcePermissions = []
      state.currentProfilePermissions = []
      state.status = 'idle'
      state.error = null
    },
    clearPermissionChecks: (state) => {
      state.permissionChecks = {}
      state.highestPermissions = {}
      state.checkStatus = 'idle'
    },
    clearAccessibleResources: (state) => {
      state.accessibleResources = {}
    },
  },
  extraReducers: (builder) => {
    builder
      // Grant permission
      .addCase(grantPermission.pending, (state) => {
        state.grantStatus = 'loading'
        state.error = null
      })
      .addCase(grantPermission.fulfilled, (state, action) => {
        state.grantStatus = 'succeeded'
        // Add to current resource permissions if it matches
        if (
          state.currentResourcePermissions.length > 0 &&
          state.currentResourcePermissions[0].resourceId === action.payload.resourceId
        ) {
          state.currentResourcePermissions.push(action.payload)
        }
      })
      .addCase(grantPermission.rejected, (state, action) => {
        state.grantStatus = 'failed'
        state.error = action.error.message
      })

      // Revoke permission
      .addCase(revokePermission.pending, (state) => {
        state.revokeStatus = 'loading'
        state.error = null
      })
      .addCase(revokePermission.fulfilled, (state, action) => {
        state.revokeStatus = 'succeeded'
        const { profileId, resourceId, permissionLevel } = action.payload
        // Remove from current resource permissions
        state.currentResourcePermissions = state.currentResourcePermissions.filter(
          (p) =>
            !(
              p.profile.id === profileId &&
              p.resourceId === resourceId &&
              p.permissionLevel === permissionLevel
            ),
        )
      })
      .addCase(revokePermission.rejected, (state, action) => {
        state.revokeStatus = 'failed'
        state.error = action.error.message
      })

      // Revoke all permissions
      .addCase(revokeAllPermissions.pending, (state) => {
        state.revokeStatus = 'loading'
        state.error = null
      })
      .addCase(revokeAllPermissions.fulfilled, (state, action) => {
        state.revokeStatus = 'succeeded'
        const { profileId, resourceId } = action.payload
        // Remove all permissions for this profile/resource combination
        state.currentResourcePermissions = state.currentResourcePermissions.filter(
          (p) => !(p.profile.id === profileId && p.resourceId === resourceId),
        )
      })
      .addCase(revokeAllPermissions.rejected, (state, action) => {
        state.revokeStatus = 'failed'
        state.error = action.error.message
      })

      // Check permission
      .addCase(checkPermission.pending, (state) => {
        state.checkStatus = 'loading'
      })
      .addCase(checkPermission.fulfilled, (state, action) => {
        state.checkStatus = 'succeeded'
        const { profileId, resourceType, resourceId, permissionLevel, hasPermission } =
          action.payload
        const key = `${profileId}-${resourceType}-${resourceId}-${permissionLevel}`
        state.permissionChecks[key] = hasPermission
      })
      .addCase(checkPermission.rejected, (state, action) => {
        state.checkStatus = 'failed'
        state.error = action.error.message
      })

      // Get highest permission
      .addCase(getHighestPermission.pending, (state) => {
        state.checkStatus = 'loading'
      })
      .addCase(getHighestPermission.fulfilled, (state, action) => {
        state.checkStatus = 'succeeded'
        const { profileId, resourceType, resourceId, permissionLevel } = action.payload
        const key = `${profileId}-${resourceType}-${resourceId}`
        state.highestPermissions[key] = permissionLevel
      })
      .addCase(getHighestPermission.rejected, (state, action) => {
        state.checkStatus = 'failed'
        state.error = action.error.message
      })

      // Get resource permissions
      .addCase(getResourcePermissions.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getResourcePermissions.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.currentResourcePermissions = action.payload
      })
      .addCase(getResourcePermissions.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })

      // Get profile permissions
      .addCase(getProfilePermissions.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getProfilePermissions.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.currentProfilePermissions = action.payload
      })
      .addCase(getProfilePermissions.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })

      // Get accessible resources
      .addCase(getAccessibleResources.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getAccessibleResources.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const { resourceType, resourceIds } = action.payload
        state.accessibleResources[resourceType] = resourceIds
      })
      .addCase(getAccessibleResources.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })

      // Update draft sharing
      .addCase(updateDraftSharing.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(updateDraftSharing.fulfilled, (state) => {
        state.status = 'succeeded'
      })
      .addCase(updateDraftSharing.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })

      // Update workout sharing
      .addCase(updateWorkoutSharing.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(updateWorkoutSharing.fulfilled, (state) => {
        state.status = 'succeeded'
      })
      .addCase(updateWorkoutSharing.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
  },
})

export const { clearPermissions, clearPermissionChecks, clearAccessibleResources } =
  permissionsSlice.actions

// =======================
// Selectors
// =======================

export const selectCurrentResourcePermissions = (state) =>
  state.permissions.currentResourcePermissions
export const selectCurrentProfilePermissions = (state) =>
  state.permissions.currentProfilePermissions
export const selectPermissionsStatus = (state) => state.permissions.status
export const selectPermissionsError = (state) => state.permissions.error
export const selectGrantStatus = (state) => state.permissions.grantStatus
export const selectRevokeStatus = (state) => state.permissions.revokeStatus
export const selectCheckStatus = (state) => state.permissions.checkStatus

/**
 * Selector to check if a profile has a specific permission
 * @param {string} profileId
 * @param {string} resourceType
 * @param {string} resourceId
 * @param {string} permissionLevel
 */
export const selectHasPermission = (profileId, resourceType, resourceId, permissionLevel) => {
  return (state) => {
    const key = `${profileId}-${resourceType}-${resourceId}-${permissionLevel}`
    return state.permissions.permissionChecks[key] || false
  }
}

/**
 * Selector to get the highest permission level for a profile on a resource
 * @param {string} profileId
 * @param {string} resourceType
 * @param {string} resourceId
 */
export const selectHighestPermissionLevel = (profileId, resourceType, resourceId) => {
  return (state) => {
    const key = `${profileId}-${resourceType}-${resourceId}`
    return state.permissions.highestPermissions[key] || null
  }
}

/**
 * Selector to get accessible resource IDs for a resource type
 * @param {string} resourceType
 */
export const selectAccessibleResourceIds = (resourceType) => {
  return (state) => state.permissions.accessibleResources[resourceType] || []
}

export default permissionsSlice.reducer
